<template>
  <section class="company-people-panel">
    <div class="company-people-panel__stack">
      <article class="company-people-panel__card">
        <div class="company-people-panel__card-head">
          <div>
            <p class="company-people-panel__eyebrow">Personas</p>
            <h2 class="company-people-panel__title">Contactos</h2>
          </div>

          <button
            v-if="permissions.canCreate"
            class="company-people-panel__primary-action"
            type="button"
            @click="$emit('create-contact')"
          >
            Nuevo contacto
          </button>
        </div>

        <div v-if="loading" class="company-people-panel__empty">
          Cargando contactos...
        </div>

        <div v-else-if="contacts.length" class="company-people-panel__list">
          <article
            v-for="contact in contacts"
            :key="contact.id || contact.rut"
            class="company-people-panel__person-card"
          >
            <div class="company-people-panel__person-main">
              <div class="company-people-panel__person-avatar">
                {{ getInitials(contact.name) }}
              </div>

              <div class="company-people-panel__person-copy">
                <div class="company-people-panel__person-title-row">
                  <h3 class="company-people-panel__person-name">
                    {{ contact.name || 'Sin nombre' }}
                  </h3>

                  <span
                    v-if="contact.is_primary"
                    class="company-people-panel__badge"
                  >
                    Principal
                  </span>
                </div>

                <p class="company-people-panel__person-line">
                  {{ formatRut(contact.rut) || 'Sin RUT' }}
                </p>

                <div class="company-people-panel__person-meta">
                  <span v-if="contact.email">{{ contact.email }}</span>
                  <span v-if="contact.phone">{{ contact.phone }}</span>
                </div>
              </div>
            </div>

            <div class="company-people-panel__actions">
              <button
                v-if="permissions.canEdit"
                class="company-people-panel__ghost-action"
                type="button"
                @click="$emit('edit-contact', contact)"
              >
                Editar
              </button>

              <button
                v-if="permissions.canDelete"
                class="company-people-panel__danger-action"
                type="button"
                @click="$emit('delete-contact', contact)"
              >
                Eliminar
              </button>
            </div>
          </article>
        </div>

        <div v-else class="company-people-panel__empty">
          No hay contactos registrados.
        </div>
      </article>

      <article class="company-people-panel__card">
        <div class="company-people-panel__card-head">
          <div>
            <p class="company-people-panel__eyebrow">Propiedad</p>
            <h2 class="company-people-panel__title">Socios</h2>
          </div>

          <button
            v-if="permissions.canCreate"
            class="company-people-panel__secondary-action"
            type="button"
            @click="$emit('create-partner')"
          >
            Nuevo socio
          </button>
        </div>

        <div v-if="loading" class="company-people-panel__empty">
          Cargando socios...
        </div>

        <div v-else-if="partners.length" class="company-people-panel__partner-grid">
          <article
            v-for="partner in partners"
            :key="partner.id || partner.rut"
            class="company-people-panel__partner-card"
          >
            <div class="company-people-panel__partner-top">
              <div>
                <h3 class="company-people-panel__partner-name">
                  {{ partner.name || 'Sin nombre' }}
                </h3>
                <p class="company-people-panel__partner-rut">
                  {{ formatRut(partner.rut) || 'Sin RUT' }}
                </p>
              </div>

              <div class="company-people-panel__participation">
                {{ formatParticipation(partner.participation_perc) }}
              </div>
            </div>

            <div class="company-people-panel__partner-footer">
              <span class="company-people-panel__partner-date">
                {{ formatBirthday(partner.birthday) }}
              </span>

              <div class="company-people-panel__actions">
                <button
                  v-if="permissions.canEdit"
                  class="company-people-panel__ghost-action"
                  type="button"
                  @click="$emit('edit-partner', partner)"
                >
                  Editar
                </button>

                <button
                  v-if="permissions.canDelete"
                  class="company-people-panel__danger-action"
                  type="button"
                  @click="$emit('delete-partner', partner)"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </article>
        </div>

        <div v-else class="company-people-panel__empty">
          No hay socios registrados.
        </div>
      </article>

      <article class="company-people-panel__card">
        <div class="company-people-panel__card-head">
          <div>
            <p class="company-people-panel__eyebrow">Representacion</p>
            <h2 class="company-people-panel__title">Representantes legales</h2>
          </div>

          <button
            v-if="permissions.canCreate"
            class="company-people-panel__secondary-action"
            type="button"
            @click="$emit('create-legal-representative')"
          >
            Nuevo representante
          </button>
        </div>

        <div v-if="loading" class="company-people-panel__empty">
          Cargando representantes...
        </div>

        <div v-else-if="legalRepresentatives.length" class="company-people-panel__list">
          <article
            v-for="representative in legalRepresentatives"
            :key="representative.id || representative.rut"
            class="company-people-panel__person-card"
          >
            <div class="company-people-panel__person-main">
              <div class="company-people-panel__person-avatar company-people-panel__person-avatar--legal">
                {{ getInitials(representative.name) }}
              </div>

              <div class="company-people-panel__person-copy">
                <h3 class="company-people-panel__person-name">
                  {{ representative.name || 'Sin nombre' }}
                </h3>

                <p class="company-people-panel__person-line">
                  {{ formatRut(representative.rut) || 'Sin RUT' }}
                </p>
              </div>
            </div>

            <div class="company-people-panel__actions">
              <button
                v-if="permissions.canEdit"
                class="company-people-panel__ghost-action"
                type="button"
                @click="$emit('edit-legal-representative', representative)"
              >
                Editar
              </button>

              <button
                v-if="permissions.canDelete"
                class="company-people-panel__danger-action"
                type="button"
                @click="$emit('delete-legal-representative', representative)"
              >
                Eliminar
              </button>
            </div>
          </article>
        </div>

        <div v-else class="company-people-panel__empty">
          No hay representantes legales registrados.
        </div>
      </article>

      <article class="company-people-panel__card">
        <div class="company-people-panel__card-head">
          <div>
            <p class="company-people-panel__eyebrow">Relacionamiento</p>
            <h2 class="company-people-panel__title">Otras relaciones</h2>
          </div>

          <button
            v-if="permissions.canCreate"
            class="company-people-panel__secondary-action"
            type="button"
            @click="$emit('create-other-relationship')"
          >
            Nueva relacion
          </button>
        </div>

        <div v-if="loading" class="company-people-panel__empty">
          Cargando otras relaciones...
        </div>

        <div v-else-if="otherRelationships.length" class="company-people-panel__list">
          <article
            v-for="otherRelationship in otherRelationships"
            :key="otherRelationship.id || `${otherRelationship.rut}-${otherRelationship.relationship_type}`"
            class="company-people-panel__person-card"
          >
            <div class="company-people-panel__person-main">
              <div class="company-people-panel__person-avatar company-people-panel__person-avatar--other">
                {{ getInitials(otherRelationship.name) }}
              </div>

              <div class="company-people-panel__person-copy">
                <div class="company-people-panel__person-title-row">
                  <h3 class="company-people-panel__person-name">
                    {{ otherRelationship.name || 'Sin nombre' }}
                  </h3>

                  <span
                    v-if="otherRelationship.relationship_type"
                    class="company-people-panel__badge company-people-panel__badge--other"
                  >
                    {{ otherRelationship.relationship_type }}
                  </span>
                </div>

                <p class="company-people-panel__person-line">
                  {{ formatRut(otherRelationship.rut) || 'Sin RUT' }}
                </p>

                <div class="company-people-panel__person-meta">
                  <span v-if="otherRelationship.email">{{ otherRelationship.email }}</span>
                  <span v-if="otherRelationship.phone">{{ otherRelationship.phone }}</span>
                </div>

                <p v-if="otherRelationship.notes" class="company-people-panel__person-note">
                  {{ otherRelationship.notes }}
                </p>
              </div>
            </div>

            <div class="company-people-panel__actions">
              <button
                v-if="permissions.canEdit"
                class="company-people-panel__ghost-action"
                type="button"
                @click="$emit('edit-other-relationship', otherRelationship)"
              >
                Editar
              </button>

              <button
                v-if="permissions.canDelete"
                class="company-people-panel__danger-action"
                type="button"
                @click="$emit('delete-other-relationship', otherRelationship)"
              >
                Eliminar
              </button>
            </div>
          </article>
        </div>

        <div v-else class="company-people-panel__empty">
          No hay otras relaciones registradas.
        </div>
      </article>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CompanyPeoplePanel',
  props: {
    contacts: {
      type: Array,
      default: () => []
    },
    partners: {
      type: Array,
      default: () => []
    },
    legalRepresentatives: {
      type: Array,
      default: () => []
    },
    otherRelationships: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    permissions: {
      type: Object,
      required: true
    }
  },
  methods: {
    getInitials (name) {
      if (!name) return 'SN'
      return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('')
    },
    formatRut (rut) {
      if (!rut) return ''
      const raw = rut.toString().replace(/\./g, '').replace(/-/g, '')
      const body = raw.slice(0, -1)
      const dv = raw.slice(-1)
      if (!body || !dv) return rut
      return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}`
    },
    formatParticipation (value) {
      if (value === undefined || value === null || value === '') return 'Sin %'
      return `${value}%`
    },
    formatBirthday (value) {
      if (!value) return 'Sin fecha de nacimiento'
      const [year, month, day] = String(value).slice(0, 10).split('-')
      if (!year || !month || !day) return 'Sin fecha de nacimiento'
      return `Nacimiento: ${day}/${month}/${year}`
    }
  }
}
</script>

<style scoped>
.company-people-panel__stack {
  display: grid;
  gap: 1rem;
}

.company-people-panel__card {
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.58);
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 252, 247, 0.98), rgba(246, 239, 231, 0.94));
  box-shadow: 0 20px 50px rgba(43, 34, 24, 0.06);
}

.company-people-panel__card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
}

.company-people-panel__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(58, 46, 33, 0.48);
}

.company-people-panel__title {
  margin: 0;
  color: #211913;
  font-size: 1.1rem;
}

.company-people-panel__primary-action,
.company-people-panel__secondary-action,
.company-people-panel__ghost-action,
.company-people-panel__danger-action {
  min-height: 2.4rem;
  padding: 0 0.85rem;
  border-radius: 999px;
}

.company-people-panel__primary-action {
  border: 0;
  background: linear-gradient(135deg, #251d16 0%, #4c3c2d 100%);
  color: #f7f0e5;
}

.company-people-panel__secondary-action,
.company-people-panel__ghost-action {
  border: 1px solid rgba(102, 81, 57, 0.12);
  background: rgba(255, 255, 255, 0.66);
  color: #392d22;
}

.company-people-panel__danger-action {
  border: 0;
  background: rgba(185, 92, 92, 0.14);
  color: #8a3f3f;
}

.company-people-panel__list {
  display: grid;
  gap: 0.75rem;
}

.company-people-panel__person-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.6);
}

.company-people-panel__person-main {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.company-people-panel__person-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 16px;
  background: rgba(79, 120, 163, 0.12);
  color: #35587d;
  font-weight: 700;
  flex-shrink: 0;
}

.company-people-panel__person-avatar--legal {
  background: rgba(143, 108, 55, 0.14);
  color: #7d5d25;
}

.company-people-panel__person-avatar--other {
  background: rgba(56, 128, 112, 0.14);
  color: #2a6d5f;
}

.company-people-panel__person-copy {
  min-width: 0;
}

.company-people-panel__person-title-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.company-people-panel__person-name {
  margin: 0;
  color: #241c16;
  font-size: 1rem;
}

.company-people-panel__person-line {
  margin: 0.2rem 0 0;
  color: rgba(45, 35, 26, 0.62);
  font-size: 0.88rem;
}

.company-people-panel__person-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.4rem;
  color: #4c3b2d;
  font-size: 0.88rem;
}

.company-people-panel__badge {
  display: inline-flex;
  align-items: center;
  min-height: 1.6rem;
  padding: 0 0.55rem;
  border-radius: 999px;
  background: rgba(77, 140, 97, 0.12);
  color: #2f6a43;
  font-size: 0.72rem;
  font-weight: 700;
}

.company-people-panel__badge--other {
  background: rgba(56, 128, 112, 0.14);
  color: #2a6d5f;
}

.company-people-panel__person-note {
  margin: 0.45rem 0 0;
  color: rgba(45, 35, 26, 0.72);
  font-size: 0.85rem;
  line-height: 1.4;
}

.company-people-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  flex-shrink: 0;
}

.company-people-panel__partner-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
}

.company-people-panel__partner-card {
  padding: 0.95rem;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.6);
}

.company-people-panel__partner-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.9rem;
}

.company-people-panel__partner-name {
  margin: 0;
  color: #241c16;
  font-size: 1rem;
}

.company-people-panel__partner-rut {
  margin: 0.2rem 0 0;
  color: rgba(45, 35, 26, 0.62);
  font-size: 0.88rem;
}

.company-people-panel__participation {
  padding: 0.42rem 0.65rem;
  border-radius: 999px;
  background: rgba(143, 108, 55, 0.12);
  color: #7d5d25;
  font-weight: 700;
  white-space: nowrap;
}

.company-people-panel__partner-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.9rem;
  margin-top: 0.9rem;
}

.company-people-panel__partner-date {
  color: rgba(45, 35, 26, 0.58);
  font-size: 0.84rem;
}

.company-people-panel__empty {
  color: rgba(45, 35, 26, 0.58);
}

@media (max-width: 900px) {
  .company-people-panel__partner-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .company-people-panel__card-head,
  .company-people-panel__person-card,
  .company-people-panel__partner-footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .company-people-panel__actions {
    width: 100%;
  }
}
</style>

